export const state = () => ({
  activeReportProject: {},
  activeReportName: '',
  lastRunDate: {},
  letterTemplates: [],
  reportBuilderData: null,
  reportJobs: []
})

export const getters = {
  // Returns full project
  getActiveReportProject: state => state.activeReportProject,
  // Returns full project
  getActiveReportName: state => state.activeReportName,
  // Returns last report run date
  getReportRunDate: state => state.lastRunDate,
  // Returns array of project participants
  getReportProjectParticipants: state => state.activeReportProject.participants,
  // Returns array of project properties
  getReportProjectProperties: state => state.activeReportProject.properties,
  // Returns individual property by identifier
  getReportProjectPropertyById: state => propertyId => state.activeReportProject
    .properties.find(p => String(p._id) === propertyId),
  // Returns array of properties for participant
  getReportProjectPropertiesByParticipantId: state => participantId => state.activeReportProject
    .properties.find(p => String(p.participant) === participantId),
  // Returns individual participant by identifier
  getReportProjectParticipantById: state => participantId => state.activeReportProject
    .participants.find(p => String(p._id) === participantId),
  // Returns array of participants by delegator identifier
  getReportProjectParticipantsByDelegator: state => delegatorId => state.activeReportProject
    .participants.find(p => String(p.delegator) === delegatorId),
  // Letter templates
  getLetterTemplates: state => state.letterTemplates || [],
  // Report builder data (properties + categories for a project)
  getReportBuilderData: state => state.reportBuilderData,
  // Report jobs (external generator) for the current user
  getReportJobs: state => state.reportJobs || [],
  hasPendingReportJobs: state => (state.reportJobs || []).some(j => j.status === 'pending')
}

export const actions = {
  async getFullProject({ commit, dispatch }, { projectId }) {
    try {
      const project = await this.$axios.$get(`/reports/project/${projectId}`)
      if (project) {
        commit('setActiveReportProject', project)
        return project
      }
      return false
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/getFullProject', info: err },
        { root: true })
    }
  },

  setActiveReportName({ commit }, name) {
    commit('setActiveReportName', name)
  },

  setLastReportRunDate({ commit }, date) {
    commit('setLastReportRunDate', date)
  },

  // Report Builder: fetch properties + categories for a project
  async fetchReportBuilderData({ commit, dispatch }, { projectId }) {
    try {
      const data = await this.$axios.$get(`/reports/builder/${projectId}`)
      commit('setReportBuilderData', data)
      return data
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/fetchReportBuilderData', info: err },
        { root: true })
    }
  },

  // Letter Templates
  async fetchLetterTemplates({ commit, dispatch }) {
    try {
      const templates = await this.$axios.$get('/reports/letter-templates')
      commit('setLetterTemplates', templates)
      return templates
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/fetchLetterTemplates', info: err },
        { root: true })
    }
  },

  async createLetterTemplate({ commit, dispatch }, { name, body }) {
    try {
      const template = await this.$axios.$post('/reports/letter-templates', { name, body })
      commit('addLetterTemplate', template)
      return template
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/createLetterTemplate', info: err },
        { root: true })
    }
  },

  async updateLetterTemplate({ commit, dispatch }, { templateId, name, body }) {
    try {
      const template = await this.$axios.$put(`/reports/letter-templates/${templateId}`, { name, body })
      commit('updateLetterTemplate', template)
      return template
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/updateLetterTemplate', info: err },
        { root: true })
    }
  },

  async deleteLetterTemplate({ commit, dispatch }, { templateId }) {
    try {
      await this.$axios.$delete(`/reports/letter-templates/${templateId}`)
      commit('removeLetterTemplate', templateId)
      return true
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/deleteLetterTemplate', info: err },
        { root: true })
    }
  },

  // Submit report configuration to the server
  async submitReportConfig({ dispatch }, config) {
    try {
      const result = await this.$axios.$post('/reports/config', config)
      return result
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/submitReportConfig', info: err },
        { root: true })
    }
  },

  // Kick off external report generation for a saved ReportConfig
  async submitReportJob({ commit, dispatch }, { reportConfigId }) {
    try {
      const job = await this.$axios.$post('/reports/jobs', { reportConfigId })
      if (job && job._id) {
        commit('addReportJob', job)
      }
      return job
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/submitReportJob', info: err },
        { root: true })
    }
  },

  async fetchReportJobs({ commit, dispatch }) {
    try {
      const jobs = await this.$axios.$get('/reports/jobs')
      commit('setReportJobs', jobs || [])
      return jobs
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/fetchReportJobs', info: err },
        { root: true })
    }
  },

  async deleteReportJob({ commit, dispatch }, { jobId }) {
    try {
      await this.$axios.$delete(`/reports/jobs/${jobId}`)
      commit('removeReportJob', jobId)
      return true
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/deleteReportJob', info: err },
        { root: true })
    }
  },

  async retryReportJob({ commit, dispatch }, { jobId }) {
    try {
      const job = await this.$axios.$post(`/reports/jobs/${jobId}/retry`)
      if (job && job._id) {
        commit('updateReportJob', { jobId, changes: job })
      }
      return job
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/retryReportJob', info: err },
        { root: true })
    }
  },

  async archiveReportJob({ commit, dispatch }, { jobId, archived }) {
    try {
      await this.$axios.$patch(`/reports/jobs/${jobId}/archive`, { archived })
      commit('updateReportJob', { jobId, changes: { archived } })
      return true
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/archiveReportJob', info: err },
        { root: true })
    }
  },

  // Fetch organization logo URL for the current user
  async fetchOrgLogoUrl({ dispatch }) {
    try {
      const result = await this.$axios.$get('/reports/org-logo')
      return result && result.logoUrl ? result.logoUrl : null
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'reports/fetchOrgLogoUrl', info: err },
        { root: true })
    }
  }
}

export const mutations = {
  init(state) {
    state.activeReportProject = {}
    state.activeReportName = ''
    state.lastRunDate = {}
    state.letterTemplates = []
    state.reportBuilderData = null
    state.reportJobs = []
  },
  setActiveReportName(state, name) {
    state.activeReportName = name
  },
  setActiveReportProject(state, project) {
    state.activeReportProject = project
  },
  setLastReportRunDate(state, date) {
    state.lastRunDate = date
  },
  setLetterTemplates(state, templates) {
    state.letterTemplates = templates
  },
  addLetterTemplate(state, template) {
    state.letterTemplates = [template, ...state.letterTemplates]
  },
  updateLetterTemplate(state, updated) {
    const idx = state.letterTemplates.findIndex(t => t._id === updated._id)
    if (idx !== -1) {
      state.letterTemplates.splice(idx, 1, updated)
    }
  },
  removeLetterTemplate(state, templateId) {
    state.letterTemplates = state.letterTemplates.filter(t => t._id !== templateId)
  },
  setReportBuilderData(state, data) {
    state.reportBuilderData = data
  },
  setReportJobs(state, jobs) {
    state.reportJobs = jobs
  },
  addReportJob(state, job) {
    state.reportJobs = [job, ...state.reportJobs]
  },
  removeReportJob(state, jobId) {
    state.reportJobs = state.reportJobs.filter(j => j._id !== jobId)
  },
  updateReportJob(state, { jobId, changes }) {
    const idx = state.reportJobs.findIndex(j => j._id === jobId)
    if (idx !== -1) {
      state.reportJobs.splice(idx, 1, { ...state.reportJobs[idx], ...changes })
    }
  }
}
